<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import type { EChartsType } from 'echarts/core'
import { api, money } from '../api.js'
import { recentRange } from '../stats-range.js'
import type { Summary } from '../types.js'

type Stats = { start: string; end: string; days: Array<Summary & { date: string }>; totals: Summary; categories: Array<{ category: string; amountFen: string }> }
type RangeMode = 'last7' | 'last30' | 'custom' | 'all'

const emit = defineEmits<{ openDay: [date: string] }>()
const today = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Shanghai' }).format(new Date())
const mode = ref<RangeMode>('last7')
const selectedStart = ref(recentRange(today, 7).start)
const selectedEnd = ref(today)
const allBounds = ref<{ start: string; end: string } | null>(null)
const chartType = ref<'line' | 'bar'>('bar')
const stats = ref<Stats | null>(null)
const loading = ref(false)
const error = ref('')
type WeatherCondition = 'sunny' | 'rainy' | null
type WeatherResult = { configured: boolean; location: string; days: Record<string, WeatherCondition>; unavailable: boolean; source: string }
const weather = ref<WeatherResult | null>(null)
const weatherLoading = ref(false)
const weatherError = ref('')
const weatherLabel = (date: string) => weather.value?.days[date] === 'rainy' ? '雨' : weather.value?.days[date] === 'sunny' ? '晴' : '—'
const chartDays = computed(() => stats.value?.days ?? [])
const chartEl = ref<HTMLDivElement>()
let chart: EChartsType | undefined
let resizeObserver: ResizeObserver | undefined
let requestSequence = 0

const range = computed(() => {
  if (mode.value === 'all') return allBounds.value ?? { start: today, end: today }
  return { start: selectedStart.value, end: selectedEnd.value }
})
const startInput = computed({ get: () => range.value.start, set: (value: string) => { selectedStart.value = value } })
const endInput = computed({ get: () => range.value.end, set: (value: string) => { selectedEnd.value = value } })

const rangeCaption = computed(() => {
  const count = Math.floor((new Date(`${range.value.end}T12:00:00`).getTime() - new Date(`${range.value.start}T12:00:00`).getTime()) / 86400000) + 1
  return `${range.value.start} — ${range.value.end} · ${Math.max(0, count)}天`
})

async function load() {
  const sequence = ++requestSequence
  loading.value = true
  error.value = ''
  try {
    if (mode.value === 'all' && !allBounds.value) allBounds.value = await api<{ start: string; end: string }>('/api/stats/bounds')
    if (range.value.start > range.value.end) throw new Error('开始日期不能晚于结束日期')
    const next = await api<Stats>(`/api/stats?start=${range.value.start}&end=${range.value.end}`)
    if (sequence !== requestSequence) return
    stats.value = next
    weather.value = null
    weatherError.value = ''
    await nextTick()
    await draw()
    void loadWeather(sequence, next.start, next.end)
  } catch (reason) {
    if (sequence === requestSequence) error.value = (reason as Error).message
  } finally {
    if (sequence === requestSequence) loading.value = false
  }
}

async function loadWeather(sequence: number, start: string, end: string) {
  weatherLoading.value = true
  try {
    // Bound each provider query, including when the ledger covers several years.
    let cursor = start
    while (cursor <= end) {
      const chunkEnd = new Date(Math.min(Date.parse(end), Date.parse(cursor) + 365 * 86400000)).toISOString().slice(0, 10)
      const result = await api<WeatherResult>(`/api/weather?start=${cursor}&end=${chunkEnd}`)
      if (sequence !== requestSequence) return
      weather.value = { ...result, days: { ...weather.value?.days, ...result.days } }
      if (result.unavailable) weatherError.value = '部分天气获取失败，可重试'
      if (!result.configured) break
      cursor = new Date(Date.parse(chunkEnd) + 86400000).toISOString().slice(0, 10)
    }
  } catch {
    if (sequence === requestSequence) weatherError.value = '天气暂时无法获取，可重试'
  } finally {
    if (sequence === requestSequence) { weatherLoading.value = false; await draw() }
  }
}

async function draw() {
  if (!chartEl.value || !stats.value) return
  const echarts = await import('echarts/core')
  const { LineChart, BarChart } = await import('echarts/charts')
  const { GridComponent, TooltipComponent, LegendComponent } = await import('echarts/components')
  const { CanvasRenderer } = await import('echarts/renderers')
  echarts.use([LineChart, BarChart, GridComponent, TooltipComponent, LegendComponent, CanvasRenderer])
  if (chart && chart.getDom() !== chartEl.value) { chart.dispose(); chart = undefined }
  const activeChart = chart ??= echarts.init(chartEl.value)
  const type = chartType.value
  activeChart.setOption({
    animation: false,
    color: ['#2f694f', '#d95b38', '#d8a136'],
    tooltip: { trigger: 'axis', confine: true, valueFormatter: (value: unknown) => `¥${Number(value).toFixed(2)}` },
    legend: { bottom: 0, itemWidth: 12, itemHeight: 8, textStyle: { color: '#5e6258', fontSize: 11 } },
    grid: { top: 18, left: 6, right: 8, bottom: 42, containLabel: true },
    xAxis: { type: 'category', data: chartDays.value.map(day => day.date), axisLabel: { hideOverlap: true, fontSize: 10, lineHeight: 18, formatter: (date: string) => `${date.slice(5)}\n${weatherLabel(date)}` }, axisLine: { lineStyle: { color: '#d9d3c7' } } },
    yAxis: { type: 'value', axisLabel: { fontSize: 10, formatter: (value: number) => value >= 1000 ? `${value / 1000}k` : value }, splitLine: { lineStyle: { color: '#ece7dc' } } },
    series: [
      { name: '销售', type, data: chartDays.value.map((day) => Number(day.salesIncomeFen) / 100), smooth: type === 'line', barMaxWidth: 14, symbolSize: 5 },
      { name: '支出', type, data: chartDays.value.map((day) => Number(day.totalExpenseFen) / 100), smooth: type === 'line', barMaxWidth: 14, symbolSize: 5 },
      { name: '结余', type, data: chartDays.value.map((day) => Number(day.balanceFen) / 100), smooth: type === 'line', barMaxWidth: 14, symbolSize: 5 },
    ],
  }, true)
  activeChart.resize()
  if (!resizeObserver) {
    resizeObserver = new ResizeObserver(() => chart?.resize())
    resizeObserver.observe(chartEl.value)
  }
}

function chooseMode(next: RangeMode) {
  mode.value = next
  if (next === 'last7') { const value = recentRange(today, 7); selectedStart.value = value.start; selectedEnd.value = value.end }
  if (next === 'last30') { const value = recentRange(today, 30); selectedStart.value = value.start; selectedEnd.value = value.end }
}
function customDateChanged() { mode.value = 'custom' }
function resize() { chart?.resize() }
function exportExcel() { location.href = `/api/export.xlsx?start=${range.value.start}&end=${range.value.end}` }

watch([mode, selectedStart, selectedEnd], load)
watch(chartType, draw)
onMounted(() => { void load(); addEventListener('resize', resize) })
onBeforeUnmount(() => { requestSequence++; resizeObserver?.disconnect(); chart?.dispose(); removeEventListener('resize', resize) })
</script>

<template>
  <main class="page stats-page">
    <header class="page-heading"><div><h1>收支走势</h1></div><button class="export-button" @click="exportExcel">导出 Excel</button></header>

    <div class="segmented stats-range-tabs">
      <button :class="{ active: mode === 'last7' }" @click="chooseMode('last7')">近7天</button>
      <button :class="{ active: mode === 'last30' }" @click="chooseMode('last30')">近30天</button>
      <button :class="{ active: mode === 'custom' }" @click="chooseMode('custom')">自定义</button>
      <button :class="{ active: mode === 'all' }" @click="chooseMode('all')">全部</button>
    </div>

    <div class="custom-range-picker" :class="{ disabled: mode === 'all' }">
      <label><span>开始日期</span><input v-model="startInput" type="date" :disabled="mode === 'all'" @change="customDateChanged" /></label><i>至</i><label><span>结束日期</span><input v-model="endInput" type="date" :disabled="mode === 'all'" @change="customDateChanged" /></label>
    </div>
    <p class="range-helper">{{ mode === 'all' ? '已选择全部账目' : '开始和结束选同一天，就是查看单日' }}</p>
    <p class="range-caption">{{ rangeCaption }}</p>

    <p v-if="error" class="form-error">{{ error }}</p>
    <div v-if="loading && !stats" class="skeleton-card">正在计算账目…</div>

    <template v-if="stats">
      <section class="stats-total" :aria-busy="loading"><p>本期收支结余</p><strong :class="{ negative: Number(stats.totals.balanceFen) < 0 }">{{ money(stats.totals.balanceFen) }}</strong><div><span>收入 {{ money(stats.totals.salesIncomeFen) }}</span><span>支出 {{ money(stats.totals.totalExpenseFen) }}</span><span>结余率 {{ stats.totals.balanceRate === null ? '—' : `${(Number(stats.totals.balanceRate) * 100).toFixed(1)}%` }}</span></div></section>
      <section class="chart-card"><div class="chart-toolbar"><h2>每日走势</h2><div class="mini-toggle"><button :class="{active:chartType==='bar'}" @click="chartType='bar'">柱状</button><button :class="{active:chartType==='line'}" @click="chartType='line'">折线</button></div></div>
        <div ref="chartEl" class="chart" role="img" :aria-label="`每日销售、支出和结余走势图，${chartDays.length}天`"></div>
        <p v-if="weatherLoading" class="weather-note" role="status">正在获取天气…</p>
        <p v-else-if="weather && !weather.configured" class="weather-note">天气待设置店铺位置。</p>
        <p v-else-if="weatherError" class="weather-note" role="status">{{ weatherError }} <button class="text-button" @click="loadWeather(requestSequence, stats.start, stats.end)">重试</button></p>
      </section>
      <section class="category-card"><h2>支出分类</h2><div v-for="category in stats.categories" :key="category.category" class="category-row"><span>{{ category.category }}</span><div><i :style="{ width: `${Math.max(4, Number(category.amountFen) / Math.max(...stats.categories.map((x) => Number(x.amountFen))) * 100)}%` }"></i></div><b>{{ money(category.amountFen) }}</b></div><p v-if="!stats.categories.length" class="empty-copy">这段时间没有支出。</p></section>
      <section class="day-table"><h2>逐日汇总</h2><button v-for="item in stats.days" :key="item.date" class="day-row" :aria-label="`查看${item.date}详情`" @click="emit('openDay', item.date)"><b>{{ item.date.slice(5) }}</b><span>销售 {{ money(item.salesIncomeFen) }}</span><span>支出 {{ money(item.totalExpenseFen) }}</span><strong :class="{ negative: Number(item.balanceFen) < 0 }">{{ money(item.balanceFen) }}</strong><i>›</i></button></section>
    </template>
  </main>
</template>
